import { Card, CardHeader, CardBody, Text, Flex, Box } from '@backstage/ui';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { RELATION_PROVIDES_API, Entity } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { RiCodeSSlashLine } from '@remixicon/react';
import { ApiTypeBadge } from '../badges/api-type-badge.component';
import { EmptyState, Tile } from '../../../../shared/components';
import styles from './relations.module.css';

const ProvidedApisContent = ({
  entities,
  loading,
  error,
}: {
  entities: Entity[] | undefined;
  loading: boolean;
  error: Error | undefined;
}) => {
  const { t } = useTranslationRef(appTranslationRef);

  if (loading) {
    return (
      <Flex direction="column" gap="3">
        {[1, 2].map(i => (
          <Box key={i} className={styles.skeleton} style={{ height: '60px' }} />
        ))}
      </Flex>
    );
  }
  if (error) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('providedApis.error')}
      </Text>
    );
  }
  if (!entities || entities.length === 0) {
    return <EmptyState title={t('providedApis.empty')} />;
  }
  return (
    <Flex direction="column" gap="3">
      {entities.map(api => {
        const ref = `${api.kind}:${api.metadata.namespace || 'default'}/${api.metadata.name}`;
        const type = (api.spec?.type as string) || undefined;
        return (
          <Tile
            key={ref}
            href={`/catalog/${api.metadata.namespace || 'default'}/${api.kind.toLowerCase()}/${api.metadata.name}`}
            title={api.metadata.title || api.metadata.name}
            description={api.metadata.description}
            trailing={type ? <ApiTypeBadge type={type} /> : undefined}
          />
        );
      })}
    </Flex>
  );
};

export const ProvidedApisCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: RELATION_PROVIDES_API,
    kind: 'api',
  });

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiCodeSSlashLine size={18} className={styles.headerIcon} />
          <Text variant="title-small" weight="bold">
            {t('providedApis.title')}
          </Text>
          {entities && entities.length > 0 && (
            <Text variant="body-x-small" className={styles.countBadge}>
              {entities.length}
            </Text>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        <ProvidedApisContent entities={entities} loading={loading} error={error} />
      </CardBody>
    </Card>
  );
};
